<template>
  <div class="home-container">
    <!-- 头部 -->
    <header class="header">
      <h1 class="title">🎵 TikTok 登录演示</h1>
      <p class="subtitle">使用 TikTok 账号快速登录体验</p>
    </header>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 登录状态显示 -->
      <div v-if="getters.isAuthenticated.value" class="user-card">
        <div class="user-info">
          <img 
            v-if="getters.userAvatar.value" 
            :src="getters.userAvatar.value" 
            :alt="getters.userDisplayName.value"
            class="avatar"
          >
          <div class="user-details">
            <h3 class="username">{{ getters.userDisplayName.value }}</h3>
            <p class="user-id">ID: {{ state.user?.open_id }}</p>
          </div>
        </div>
        <div class="user-actions">
          <router-link to="/dashboard" class="btn btn-primary">
            进入面板
          </router-link>
          <button @click="handleLogout" class="btn btn-outline">
            退出登录
          </button>
        </div>
      </div>

      <!-- 未登录状态 -->
      <div v-else class="login-prompt">
        <div class="icon">🔐</div>
        <h2>欢迎使用 TikTok 登录</h2>
        <p class="description">
          这是一个 TikTok OAuth 2.0 登录演示应用，展示了如何使用 TikTok 账号进行第三方登录。
        </p>
        <div class="features">
          <div class="feature">
            <span class="feature-icon">🛡️</span>
            <span>安全的 OAuth 2.0 流程</span>
          </div>
          <div class="feature">
            <span class="feature-icon">🚀</span>
            <span>快速登录体验</span>
          </div>
          <div class="feature">
            <span class="feature-icon">👤</span>
            <span>获取基本用户信息</span>
          </div>
        </div>
        <router-link to="/login" class="btn btn-primary btn-large">
          开始登录
        </router-link>
      </div>

      <!-- 服务状态 -->
      <div class="service-status">
        <div class="status-item">
          <span class="status-label">后端服务:</span>
          <span :class="['status-value', healthStatusClass]">
            {{ healthStatusText }}
          </span>
        </div>
      </div>

      <!-- 法律信息 -->
      <LegalInfo />
    </main>

    <!-- 底部 -->
    <footer class="footer">
      <div class="footer-content">
        <p>&copy; 2025 TikTok 登录演示 - 仅供学习使用</p>
        <div class="footer-links">
          <router-link to="/terms-of-service" class="footer-link">服务条款</router-link>
          <span class="footer-separator">|</span>
          <router-link to="/privacy-policy" class="footer-link">隐私政策</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store'
import LegalInfo from '../components/LegalInfo.vue'

export default {
  name: 'Home',
  components: {
    LegalInfo
  },
  setup() {
    const router = useRouter()
    const { state, getters, actions } = useAuthStore()

    // 健康状态相关计算属性
    const healthStatusClass = computed(() => {
      if (!state.healthStatus) return 'status-unknown'
      return state.healthStatus.status === 'healthy' ? 'status-healthy' : 'status-error'
    })

    const healthStatusText = computed(() => {
      if (!state.healthStatus) return '检查中...'
      return state.healthStatus.status === 'healthy' ? '正常' : '异常'
    })

    // 方法
    const handleLogout = async () => {
      if (confirm('确定要退出登录吗？')) {
        actions.logout()
        router.push('/')
      }
    }

    // 初始化
    onMounted(async () => {
      await actions.initialize()
      await actions.checkHealth()
    })

    return {
      state,
      getters,
      actions,
      healthStatusClass,
      healthStatusText,
      handleLogout
    }
  }
}
</script>

<style scoped>
.home-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header {
  text-align: center;
  padding: 2rem 1rem;
  color: white;
}

.title {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
}

.user-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
}

.user-info {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-right: 1rem;
  object-fit: cover;
}

.username {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.user-id {
  color: #666;
  font-size: 0.9rem;
}

.user-actions {
  display: flex;
  gap: 1rem;
}

.login-prompt {
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
}

.icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.login-prompt h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #333;
}

.description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.features {
  display: flex;
  justify-content: space-around;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
  font-size: 0.9rem;
}

.feature-icon {
  font-size: 1.2rem;
}

.service-status {
  margin-top: 2rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
  color: white;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-healthy {
  color: #4CAF50;
}

.status-error {
  color: #f44336;
}

.status-unknown {
  color: #ff9800;
}

.footer {
  text-align: center;
  padding: 1rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.footer-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s;
}

.footer-link:hover {
  color: white;
  text-decoration: underline;
}

.footer-separator {
  color: rgba(255, 255, 255, 0.5);
}

/* 按钮样式 */
.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 1rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .title {
    font-size: 2rem;
  }
  
  .user-info {
    flex-direction: column;
    text-align: center;
  }
  
  .avatar {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .user-actions {
    flex-direction: column;
  }
  
  .features {
    flex-direction: column;
    align-items: center;
  }
}
</style> 